import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, TriangleAlert } from 'lucide-react';

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

import { DeliveryType, type GetOrphanBoxDeliveriesQuery, useGetOrphanBoxDeliveriesQuery } from 'data/__generated__';
import { formatDateForDisplay } from 'helpers/dateTimes';

import { BundleTypeTag } from './TypeTag';

/** Une box ajoutée après le lancement de son flux doit remonter sans recharger la page. */
const POLL_INTERVAL_MS = 60_000;

const STORE_DELIVERY_TYPES = [DeliveryType.StockTransfer, DeliveryType.PackageDelivery];

type OrphanBoxDelivery = GetOrphanBoxDeliveriesQuery['orphanBoxDeliveries'][number];

/**
 * Les livraisons de la préparation en cours dont des box ne sont dans aucun bundle alors que leur flux a
 * déjà été lancé. Indépendant de la chip « Date de préparation » : c'est l'état du jour, pas un filtre.
 */
export const OrphanBoxesWarning = (): JSX.Element | null => {
    const [isOpen, setIsOpen] = useState(true);

    // Pas de `throw` sur erreur, contrairement au reste de la page : ce bandeau est un garde-fou, une
    // lecture ratée ne doit pas emporter la liste des bundles avec elle.
    const { data } = useGetOrphanBoxDeliveriesQuery({
        pollInterval: POLL_INTERVAL_MS,
        fetchPolicy: 'cache-and-network',
    });

    const orphanBoxDeliveries = data?.orphanBoxDeliveries ?? [];

    if (!orphanBoxDeliveries.length) {
        return null;
    }

    const boxCount = orphanBoxDeliveries.reduce((total, { boxes }) => total + boxes.length, 0);

    return (
        <Collapsible
            open={isOpen}
            onOpenChange={setIsOpen}
            className="mx-4 mt-4 rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-800 dark:text-amber-300"
            data-test="orphan-boxes-warning"
        >
            <CollapsibleTrigger className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left">
                <TriangleAlert size={18} className="shrink-0" />
                <span className="text-sm font-semibold">
                    {boxCount} box hors bundle sur {orphanBoxDeliveries.length} livraison
                    {orphanBoxDeliveries.length > 1 ? 's' : ''}
                </span>
                <span className="text-xs opacity-80">
                    Leur flux a déjà été lancé : à ajouter à un bundle ou à relancer.
                </span>
                <ChevronDown
                    size={16}
                    className={`ml-auto shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                />
            </CollapsibleTrigger>
            <CollapsibleContent>
                <ul className="max-h-56 overflow-y-auto border-t border-amber-500/30 px-4 py-1.5">
                    {orphanBoxDeliveries.map((orphanBoxDelivery) => (
                        <OrphanBoxDeliveryRow
                            key={`${orphanBoxDelivery.delivery._id}-${orphanBoxDelivery.flow}`}
                            orphanBoxDelivery={orphanBoxDelivery}
                        />
                    ))}
                </ul>
            </CollapsibleContent>
        </Collapsible>
    );
};

const OrphanBoxDeliveryRow = ({ orphanBoxDelivery }: { orphanBoxDelivery: OrphanBoxDelivery }): JSX.Element => {
    const { delivery, flow, boxes } = orphanBoxDelivery;
    const productCount = boxes.reduce(
        (total, { products }) => total + products.reduce((boxTotal, { quantity }) => boxTotal + quantity, 0),
        0,
    );

    return (
        <li
            className="flex flex-wrap items-center gap-x-3 gap-y-1 py-1.5 text-sm text-foreground"
            data-test="orphan-box-delivery"
        >
            <BundleTypeTag type={flow} />
            <span className="font-semibold">{delivery.siteName ?? 'Site inconnu'}</span>
            {delivery.organizationName ? (
                <span className="text-muted-foreground">{delivery.organizationName}</span>
            ) : null}
            <span className="text-muted-foreground">
                {STORE_DELIVERY_TYPES.includes(delivery.type) ? 'Store' : 'Freefood'} · livrée le{' '}
                {formatDateForDisplay(delivery.deliveryDate, { displayDay: true })}
                {delivery.roundName ? ` · ${delivery.roundName}` : ''}
            </span>
            <span>
                {boxes.map(({ number }) => `n°${number}`).join(', ')} · {productCount} produit
                {productCount > 1 ? 's' : ''}
            </span>
            <Link to={`/delivery/${delivery._id}`} className="ml-auto text-xs underline">
                Fiche livraison
            </Link>
        </li>
    );
};
